import React, { useState, useEffect, useRef } from "react";
import { evaluateSources, calculateBRI, THRESHOLDS, THRESHOLD_OPERATORS, SOURCE_GEN4, SOURCE_GEN3 } from "./engine/index.js";
import CalibrationPage from "./CalibrationPage.js";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Mono:ital,wght@0,400;0,700;1,400;1,700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --bg: #050505;
  --panel: #0a0a0a;
  --line: #1e1e1e;
  --line-bright: #333333;
  --text-main: #f0f0f0;
  --text-dim: #555555;
  --text-mid: #888888;
  --accent-red: #ff2a2a;
  --accent-green: #00ff66;
  --accent-amber: #ffb300;
  --accent-blue: #00aaff;
  --font-mono: 'Space Mono', 'Courier New', Courier, ui-monospace, SFMono-Regular, monospace;
}

html, body, #root {
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  height: 100vh; width: 100%;
  background: var(--bg); color: var(--text-main);
  font-family: var(--font-mono); font-size: 11px;
  text-transform: uppercase; overflow: hidden;
}

body::after {
  content: ''; position: fixed; inset: 0;
  background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px);
  pointer-events: none; z-index: 9999;
}

body::before {
  content: ''; position: fixed; inset: 0;
  background: linear-gradient(rgba(51,51,51,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(51,51,51,0.12) 1px, transparent 1px);
  background-size: 24px 24px; pointer-events: none; z-index: 0;
}

.shell {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 12px;
  /* Reserves room for .bottom-nav, which is position:fixed (see below) and
     therefore no longer part of this flex column's own height math -- without
     this, fixed nav would sit on top of whatever content happens to be at the
     true bottom of the viewport instead of after it. Kept as a constant here
     (not measured) since the nav's own height is fixed by its CSS below. */
  padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px));
  position: relative;
  z-index: 1;
  gap: 16px;
  box-sizing: border-box;
  overflow: hidden;
}

.header {
  display: flex; justify-content: space-between; align-items: flex-start;
  border-bottom: 2px solid var(--text-main); padding-bottom: 10px; overflow: hidden;
}

.brand-wrap { display: flex; flex-direction: column; gap: 2px; }
.brand { font-size: 13px; font-weight: 700; letter-spacing: 2px; }

.header-right { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
.header-top-row { display: flex; gap: 12px; align-items: center; }
.live-badge { display: flex; align-items: center; gap: 6px; font-size: 9px; letter-spacing: 2px; }
.btn-signout {
  background: transparent; border: 1px solid var(--line-bright); color: var(--text-dim);
  font-family: var(--font-mono); font-size: 8px; letter-spacing: 1px; padding: 6px 14px;
  cursor: pointer; text-transform: uppercase;
  /* Fitt's law: text/border stay small on purpose (rare, low-stakes action,
     tucked in a corner of a deliberately compact header) but the hit area
     itself is padded well past the visible box -- min-height/min-width give
     a ~36px tall, 64px wide tap target on touch instead of the ~14x30px box
     the padding alone implied. Not the full 44px HIG minimum -- this is a
     single-user app, not a public product, and 44px here would visually
     dominate a header everything else in it keeps deliberately tiny. */
  min-height: 36px; min-width: 64px;
  display: inline-flex; align-items: center; justify-content: center;
}
.btn-signout:hover { color: var(--text-main); border-color: var(--text-main); }
.blink { width: 7px; height: 7px; border-radius: 50%; animation: pulse 1.5s infinite; }
@keyframes pulse { 0%,100% { opacity: 1; box-shadow: 0 0 8px currentColor; } 50% { opacity: 0.15; box-shadow: none; } }

.telemetry-grid {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px; background: var(--line); border: 1px solid var(--line-bright);
}
.tel-block { background: var(--panel); padding: 8px; position: relative; overflow: hidden; }
.tel-label { font-size: 8px; color: var(--text-dim); letter-spacing: 1px; margin-bottom: 6px; line-height: 1.3; }
.tel-value { font-size: 18px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; }
.tel-unit { font-size: 10px; color: var(--text-dim); }
.tel-bar-wrap { height: 2px; background: var(--line); margin: 8px 0; }
.tel-bar { height: 100%; transition: width 1s ease, background 0.5s; }
.tel-meta { font-size: 8px; color: var(--text-dim); letter-spacing: 0.2px; margin: 3px 0 2px; line-height: 1.4; }
.tel-source { font-size: 8px; color: var(--accent-blue); letter-spacing: 1px; margin-top: 2px; }
.tel-stale { opacity: 0.65; }
.tel-tap-hint { font-size: 8px; color: var(--text-dim); letter-spacing: 1px; margin-top: 4px; }

@keyframes glucosePulse {
  0%, 100% { border-color: #ffb300; }
  50% { border-color: #333333; }
}
.glucose-pulse {
  border: 1px solid #ffb300;
  animation: glucosePulse 1s ease-in-out infinite;
}

.glucose-entry { display: flex; flex-direction: column; gap: 6px; margin-top: 4px; }
.glucose-input {
  background: transparent; border: 1px solid var(--line-bright); color: var(--accent-green);
  font-family: var(--font-mono); font-size: 16px; font-weight: 700; padding: 4px 6px;
  width: 100%; outline: none; -moz-appearance: textfield;
}
.glucose-input:focus { border-color: var(--accent-green); }
.glucose-input::-webkit-outer-spin-button,
.glucose-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.glucose-submit {
  background: var(--text-main); color: var(--bg); border: none;
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 2px; padding: 5px; cursor: pointer; text-transform: uppercase;
}

.command-wrap {
  display: flex; flex-direction: column; justify-content: center; align-items: center;
  border: 2px solid var(--line-bright); padding: 14px 16px; text-align: center;
  background: var(--panel); position: relative; overflow: visible; transition: border-color 0.5s;
  flex-shrink: 0;
}
.corner { position: absolute; width: 14px; height: 14px; border: 1px solid var(--text-dim); }
.tl { top: 4px; left: 4px; border-right: 0; border-bottom: 0; }
.tr { top: 4px; right: 4px; border-left: 0; border-bottom: 0; }
.bl { bottom: 4px; left: 4px; border-right: 0; border-top: 0; }
.br { bottom: 4px; right: 4px; border-left: 0; border-top: 0; }

.cmd-meta { font-size: 9px; color: var(--text-dim); margin-bottom: 10px; letter-spacing: 2px; }
.cmd-text { font-size: 16px; font-weight: 700; margin-bottom: 10px; transition: color 0.5s; max-width: 100%; line-height: 1.3; }
.cmd-rationale { font-size: 10px; color: var(--text-mid); line-height: 1.6; max-width: 100%; margin-bottom: 18px; letter-spacing: 0.5px; }
.cmd-briefing { font-size: 11px; color: var(--text-dim); line-height: 1.7; max-width: 100%; margin-bottom: 14px; letter-spacing: 0.3px; border-left: 1px solid var(--text-dim); padding-left: 10px; }

.btn-execute {
  background: var(--text-main); color: var(--bg); border: none; padding: 12px 20px;
  font-family: var(--font-mono); font-size: 11px; font-weight: 700; letter-spacing: 3px;
  cursor: pointer; box-shadow: 4px 4px 0 var(--accent-amber);
  transition: transform 0.08s, box-shadow 0.08s;
}
.btn-execute:hover { transform: translate(2px,2px); box-shadow: 2px 2px 0 var(--accent-amber); }
.btn-execute:active { transform: translate(4px,4px); box-shadow: none; }
.btn-execute.done { background: var(--line-bright); color: var(--text-dim); box-shadow: none; cursor: not-allowed; transform: none; }

.optimal-label { color: var(--accent-green); font-weight: 700; letter-spacing: 3px; font-size: 11px; animation: breathe 3s infinite; }
@keyframes breathe { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }

.sys-log {
  flex: 1;
  overflow-y: auto;
  border-top: 1px solid var(--line-bright);
  padding-top: 8px;
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-end;
  gap: 2px;
  min-height: 100px;
}
/* No reserved floor when there's nothing to show -- a quiet page-load
   shouldn't hold open a 100px+ region for events that haven't happened
   yet. Restores itself the moment a real event lands. */
.sys-log-empty {
  flex: 0 0 auto;
  min-height: 0;
  border-top: none;
  padding-top: 0;
}
.log-line { font-size: 9px; color: var(--text-dim); display: flex; gap: 12px; animation: slideIn 0.25s ease; }
@keyframes slideIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
@keyframes fadeIn { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: translateY(0); } }
.log-time { color: var(--accent-amber); min-width: 80px; flex-shrink: 0; }
.log-roche { color: var(--accent-blue); }
.log-cursor { display: inline-block; width: 6px; height: 9px; background: var(--accent-amber); animation: blink-cursor 1s step-end infinite; margin-left: 2px; vertical-align: middle; }
@keyframes blink-cursor { 0%,100% { opacity: 1; } 50% { opacity: 0; } }

.history-log { border-top: 1px solid var(--line-bright); padding-top: 8px; margin-top: 8px; flex-shrink: 0; }
.history-log-header { font-size: 9px; color: var(--text-dim); letter-spacing: 1px; margin-bottom: 6px; }
.history-log-line { font-size: 9px; color: var(--text-dim); display: flex; gap: 12px; padding: 1px 0; }
.history-log-date { color: var(--accent-amber); min-width: 80px; flex-shrink: 0; }

.telemetry-toggle {
  display: flex; justify-content: flex-end; align-items: center; gap: 8px;
  padding: 6px 4px; font-size: 9px; letter-spacing: 1px; color: var(--text-dim);
  cursor: pointer; user-select: none; flex-shrink: 0;
}
.telemetry-toggle:hover { color: var(--text-mid); }
.telemetry-toggle .dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--accent-blue); box-shadow: 0 0 4px var(--accent-blue);
}
.telemetry-panel {
  border: 1px solid var(--line-bright); background: var(--panel);
  margin-bottom: 6px; flex-shrink: 0;
}
.telemetry-inner { padding: 10px 14px; font-size: 9px; color: var(--text-mid); }
.telemetry-header { color: var(--text-main); letter-spacing: 1px; margin-bottom: 8px; }
.telemetry-divider { border-top: 1px solid var(--line-bright); margin: 8px 0; }
.telemetry-raw-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; }
.telemetry-raw-grid .label { color: var(--text-dim); }
.telemetry-raw-grid .value { color: var(--accent-amber); }
.telemetry-tag { color: var(--text-dim); font-size: 8px; }
.telemetry-stages { margin-top: 8px; color: var(--text-dim); letter-spacing: 0.3px; }

.auth-overlay {
  position: fixed; inset: 0; background: var(--bg); z-index: 10000;
  display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 16px;
  font-family: 'Space Mono', 'Courier New', Courier, monospace;
  padding: env(safe-area-inset-top) 12px env(safe-area-inset-bottom); box-sizing: border-box; overflow: hidden;
}
.auth-title { font-size: 13px; color: var(--accent-amber); letter-spacing: 4px; font-weight: 700; }
.auth-input {
  background: transparent; border: 1px solid var(--line-bright); color: var(--accent-green);
  font-family: var(--font-mono); font-size: 14px; padding: 10px; text-align: center;
  letter-spacing: 4px; width: 80vw; max-width: 300px; outline: none; transition: border-color 0.2s;
}
.auth-input:focus { border-color: var(--accent-green); }
.auth-hint { font-size: 9px; color: var(--text-dim); letter-spacing: 2px; }
.auth-decrypt { font-family: var(--font-mono); font-size: 11px; letter-spacing: 3px; font-weight: 700; padding: 12px; background: var(--text-main); color: var(--bg); border: none; cursor: pointer; box-shadow: 3px 3px 0 var(--accent-amber); margin-top: 8px; text-transform: uppercase; width: 80vw; max-width: 300px; }
.auth-error { font-size: 9px; color: var(--accent-red); letter-spacing: 2px; animation: fadeIn 0.2s ease; }

/* Fixed, not a normal flex child -- so it always sits at the true
   bottom of the viewport regardless of how much content is above it
   (a plain in-flow flex child only lands there when a sibling happens
   to flex-grow and fill the remaining space, which isn't reliably true
   across every content state -- empty log, raw telemetry open/closed,
   0 vs. N Calibration panels). .shell's padding-bottom above reserves
   the room so scrollable content never renders underneath it. */
.bottom-nav {
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 100;
  display: grid; grid-template-columns: 1fr 1fr; gap: 2px;
  background: var(--line); border-top: 1px solid var(--line-bright);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
.bottom-nav-tab {
  min-height: 40px; display: flex; align-items: center; justify-content: center;
  background: var(--panel); border: none; border-top: 2px solid transparent;
  color: var(--text-mid); font-family: var(--font-mono); font-size: 10px;
  letter-spacing: 2px; cursor: pointer;
}
.bottom-nav-tab.active { border-top-color: var(--accent-green); color: var(--accent-green); }

/* Extra bottom padding here, separate from .shell's own padding-bottom
   reservation above -- .shell's padding only guarantees SOMETHING isn't
   literally hidden behind the fixed nav; it doesn't give this page's own
   last child (the genome box) any breathing room at the end of ITS OWN
   scroll area, so the box's bottom border landed flush against the nav
   with zero visual gap and read as clipped. Matches the nav's own
   height so the gap is consistent whether or not the safe-area inset
   is nonzero. */
.calibration-page {
  flex: 1; overflow-y: auto; display: flex; flex-direction: column;
  padding-bottom: 48px;
}
.calibration-subheader {
  display: flex; justify-content: space-between; align-items: flex-end;
  padding: 14px 0 10px 0; flex-shrink: 0;
}
.calibration-subheader-label { font-size: 9px; letter-spacing: 2px; color: var(--text-dim); margin-bottom: 6px; }
.calibration-tagline { font-family: 'Space Mono', monospace; font-weight: 700; font-size: 11px; letter-spacing: 2px; color: var(--accent-green); }
.btn-add-panel {
  min-height: 32px; padding: 0 12px; background: var(--panel); color: var(--text-main);
  border: 1px solid var(--line-bright); font-family: var(--font-mono); font-size: 10px;
  letter-spacing: 1px; cursor: not-allowed; opacity: 0.5;
}
.calibration-panel-count {
  font-size: 9px; letter-spacing: 2px; color: var(--text-dim);
  border-top: 1px solid var(--line-bright); padding: 10px 0; flex-shrink: 0;
}
.panel-card { background: var(--line); border: 1px solid var(--line-bright); display: flex; flex-direction: column; gap: 2px; margin-bottom: 12px; flex-shrink: 0; }
.panel-meta { background: var(--panel); padding: 12px; display: flex; flex-direction: column; gap: 6px; }
.panel-meta-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.panel-date { font-family: 'Space Mono', monospace; font-weight: 700; font-size: 16px; color: var(--accent-amber); }
.panel-age { font-size: 10px; letter-spacing: 1px; color: var(--text-mid); }
.panel-source-row { display: flex; gap: 18px; font-size: 9px; letter-spacing: 0.5px; color: var(--text-dim); flex-wrap: wrap; }
.panel-source-badge { color: var(--accent-blue); }
.panel-lab-flags { font-size: 9px; letter-spacing: 1px; color: var(--accent-amber); }
.panel-uncatalogued-flag { font-size: 9px; letter-spacing: 1px; color: var(--accent-red); }
.panel-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 2px; }
.panel-column { background: var(--panel); display: flex; flex-direction: column; gap: 10px; padding: 10px 12px 12px; }
.marker-group-name { font-size: 9px; letter-spacing: 2px; color: var(--text-dim); padding-bottom: 6px; }
.marker-row { display: grid; grid-template-columns: minmax(0, 1.2fr) 70px minmax(40px, 1fr) 60px; align-items: center; column-gap: 10px; min-height: 22px; border-top: 1px solid var(--line); padding-top: 2px; }
.marker-label { font-size: 10px; color: var(--text-main); }
.marker-value { font-family: 'Space Mono', monospace; font-weight: 700; font-size: 12px; }
.marker-flag { font-size: 9px; margin-left: 4px; }
.marker-range { font-size: 9px; color: var(--text-dim); text-align: right; }
.marker-range-bar { position: relative; height: 10px; }
.marker-range-bar-track { position: absolute; left: 0; right: 0; top: 4px; height: 2px; background: var(--line-bright); }
.marker-range-bar-zone { position: absolute; left: 20%; width: 60%; top: 3px; height: 4px; background: #1e3a22; }
.marker-range-bar-dot { position: absolute; top: 1px; width: 8px; height: 8px; margin-left: -4px; }
.marker-range-bar-tick { position: absolute; top: -1px; width: 2px; height: 12px; margin-left: -1px; background: var(--text-dim); }
.panel-trend-footer { background: var(--panel); padding: 8px 12px; font-size: 9px; letter-spacing: 1px; color: var(--text-dim); }
.genome-section-label {
  font-size: 9px; letter-spacing: 2px; color: var(--text-dim);
  border-top: 1px solid var(--line-bright); margin-top: 16px; padding: 10px 0; flex-shrink: 0;
}
.genome-box { background: var(--panel); border: 1px solid var(--line-bright); padding: 12px; display: flex; justify-content: space-between; align-items: center; flex-shrink: 0; gap: 12px; flex-wrap: wrap; }
.genome-label { font-size: 9px; letter-spacing: 2px; color: var(--text-dim); }
.genome-value { font-family: 'Space Mono', monospace; font-weight: 700; font-size: 22px; color: var(--text-dim); }
.genome-unit { font-family: 'Space Mono', monospace; font-weight: 700; font-size: 10px; color: var(--text-mid); margin-left: 8px; }
.genome-status { font-size: 9px; letter-spacing: 1px; color: var(--text-mid); margin-top: 4px; }
.btn-import-genome {
  min-height: 36px; padding: 0 14px; background: var(--panel); color: var(--text-main);
  border: 1px solid var(--line-bright); font-family: var(--font-mono); font-size: 10px;
  letter-spacing: 1px; cursor: not-allowed; opacity: 0.5;
}
.calibration-empty { font-size: 10px; color: var(--text-dim); letter-spacing: 1px; padding: 20px 0; text-align: center; }

@media (max-width: 480px) {
  .panel-columns { grid-template-columns: 1fr; }
  /* Bar visual drops out at phone width -- not enough room to be
     legible; label/value/range-text still fit. */
  .marker-row { grid-template-columns: minmax(0, 1.2fr) 80px 60px; }
  .marker-range-bar { display: none; }
}
`;

const STALE_HRS = 12;

// Module scope, not component scope -- pure function, no dependency on
// anything from MethuselahFinal, safe to hoist. Used by addLog() and by
// the isolated Clock component below.
const ts = () => new Date().toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });

// 2026-09-21 (INP audit): this used to be a `clock` state variable ticking
// once a second at the TOP of MethuselahFinal (a ~1000-line component),
// which meant every second, forever, the entire tile grid / raw telemetry
// panel / weekly history log re-rendered too -- purely to update this one
// "// HH:MM:SS" string. Isolating the tick into its own tiny component
// scopes that per-second re-render to just this, so a real user
// interaction elsewhere in the tree no longer has to compete with a
// whole-tree re-render it has nothing to do with. This is a real,
// identifiable cause of the class of problem INP measures -- not
// something verified by an actual Lighthouse/field-data run in this
// environment (no live browser tooling here); see known_issues.md,
// 2026-09-21 for that caveat stated plainly.
function Clock() {
  const [clock, setClock] = useState(ts());
  useEffect(() => {
    const t = setInterval(() => setClock(ts()), 1000);
    return () => clearInterval(t);
  }, []);
  return clock;
}

function formatAge(iso) {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}min old`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h old`;
  return `${Math.floor(hrs / 24)}d old`;
}

function isStale(iso) {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() > STALE_HRS * 3600000;
}

// 2026-08-12: api/gen3-bridge.js's GET, and api/glucose.js's / api/vector-
// history.js's GET+POST, had no protection at all -- anyone with the URL
// could read (two of the three, also write) real personal health data
// without ever seeing the login screen. The DASHBOARD_ACCESS_KEY login only
// ever gated the React UI, not the data underneath it. authedFetch attaches
// the same key the login screen already verified as a header, reusing one
// credential rather than inventing a second. Reads sessionStorage fresh on
// every call (not a value captured in a stale closure) since the key is
// written once, at login, from a component far from every call site here.
function authedFetch(url, options = {}) {
  const key = sessionStorage.getItem("dashboardKey") || "";
  return fetch(url, { ...options, headers: { ...(options.headers || {}), "X-Dashboard-Key": key } });
}

function getTrend(history) {
  if (!history || history.length < 2) return null;
  const cur = history[history.length - 1];
  const prev = history[history.length - 2];
  if (prev === 0) return null;
  const delta = (cur - prev) / Math.abs(prev);
  if (delta > 0.05) return "trending up";
  if (delta < -0.05) return "trending down";
  return "stable";
}

// Sanity ceiling for sleep-duration history entries -- no single day can
// plausibly exceed this many hours of sleep. Guards sleepDurationHistory
// (persisted in localStorage, never otherwise bounds-checked) against any
// bad value that ever reaches it, past or future -- e.g. the now-fixed
// 92.0-HRS production bug (see known_issues.md) could still be sitting in
// an existing browser's localStorage today, silently inflating the 7-day
// average and trend for anyone who had that bug reach their device before
// it was fixed. Applied both when loading existing history (self-heals
// old contamination) and when appending a new value (stops new bad values
// from ever being written).
const SLEEP_DURATION_PLAUSIBLE_MAX_H = 24;
const isPlausibleSleepHours = (h) => typeof h === "number" && h > 0 && h <= SLEEP_DURATION_PLAUSIBLE_MAX_H;

function avgOf(history) {
  if (!history || history.length === 0) return null;
  return history.reduce((a, b) => a + b, 0) / history.length;
}

// Predicate + meta line — reads THRESHOLDS/THRESHOLD_OPERATORS directly
// (the same single source of truth engine/index.js's evaluate() reads), so
// this can never display a rule different from the one that actually fires
// -- guarded by the THRESHOLD_OPERATORS drift tests in engine/index.test.js.
// Module scope (not inside MethuselahFinal) so GlucosePanel, a separate
// component, can build its own predicate from its own already-computed
// color rather than the parent recomputing glucose's fired state a second
// time. Predicate is colored to match the tile's own fired/passing color --
// the same signal the value above it already carries, not a second
// dim/bright scheme that could ever disagree with it. 2026-08-21.
function predicateText(label, thresholdKey, unit) {
  return `${label} ${THRESHOLD_OPERATORS[thresholdKey]} ${THRESHOLDS[thresholdKey]}${unit ? " " + unit : ""}`;
}
function metaLine(predicate, color, avg, trend) {
  const rest = [avg, trend].filter(Boolean).join(" · ");
  return <><span style={{ color }}>{predicate}</span>{rest ? ` · ${rest}` : ""}</>;
}

// Metric — 3-line tile: label / value + context / source + age
// meta: pre-built "(<colored predicate> · 7d avg Y · trend)" node, from metaLine()
// stale: dims tile + shifts source line to amber
// n/agg -- sample count + aggregation kind (Task 1b's bridge provenance
// fields). Independently nullable: n omitted with no data to count (sleep
// duration), agg omitted on a bridge that predates Task 1b. Neither is a
// placeholder -- absent means nothing is appended, not "N=?" or "--".
function nAggSuffix(n, agg) {
  const parts = [];
  if (n != null) parts.push(`N=${n.toLocaleString()}`);
  if (agg != null) parts.push(agg.toUpperCase().replace(/_/g, " "));
  return parts.length ? parts.join(" ") : null;
}

function Metric({ label, val, unit, color, meta, age, stale, source, n, agg }) {
  const sourceLabel = source === SOURCE_GEN4 ? "OURA LIVE" : source === SOURCE_GEN3 ? "GEN3 BLE" : null;
  const sourceColor = stale ? "var(--accent-amber)" : source === SOURCE_GEN3 ? "cyan" : "var(--accent-blue)";
  const nAgg = nAggSuffix(n, agg);
  return (
    <div className={`tel-block${stale ? " tel-stale" : ""}`}>
      <div className="tel-label">{label}</div>
      <div className="tel-value" style={{ color }}>
        {val} <span className="tel-unit">{unit}</span>
      </div>
      {meta && <div className="tel-meta">({meta})</div>}
      {sourceLabel
        ? <div className="tel-source" style={{ color: sourceColor }}>● {sourceLabel} · {age || "?"}{nAgg ? ` · ${nAgg}` : ""}{stale ? "  [flag: stale]" : ""}</div>
        : <div className="tel-source" style={{ color: "var(--text-dim)" }}>AWAITING DATA</div>
      }
    </div>
  );
}

function GlucosePanel({ reading, entryOpen, inputVal, avg, trend, age, stale, onTap, onBLERead, onInputChange, onKeyDown, onSubmit }) {
  const hasReading = reading !== null;
  const isElevated = hasReading && reading > THRESHOLDS.glucose;
  const color = !hasReading ? "var(--accent-amber)" : isElevated ? "var(--accent-red)" : "var(--accent-green)";
  const sourceColor = stale ? "var(--accent-amber)" : "var(--accent-blue)";
  // Predicate built here (not passed down as a pre-assembled string) so it
  // can reuse this component's own already-computed `color` -- the exact
  // fired/passing state -- instead of a second, independent read of
  // THRESHOLDS.glucose in the parent that could in principle disagree.
  const meta = hasReading
    ? metaLine(predicateText("GLUCOSE", "glucose", "mmol/l"), color, avg !== null ? `7d avg ${avg.toFixed(1)}` : null, trend)
    : null;

  return (
    <div
      className={`tel-block${!hasReading ? " glucose-pulse" : ""}${stale ? " tel-stale" : ""}`}
      onClick={!entryOpen ? onTap : undefined}
      style={{ cursor: entryOpen ? "default" : "pointer" }}
    >
      <div className="tel-label">GLYCEMIC LOAD</div>
      {entryOpen ? (
        <div className="glucose-entry">
          <input
            className="glucose-input"
            type="number"
            step="0.1"
            min="1"
            max="30"
            value={inputVal}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            autoFocus
            placeholder="0.0"
          />
          <button className="glucose-submit" onClick={e => { e.stopPropagation(); onSubmit(); }}>
            LOG
          </button>
        </div>
      ) : (
        <>
          <div className="tel-value" style={{ color }}>
            {hasReading ? reading.toFixed(1) : "--"} <span className="tel-unit">mmol/L</span>
          </div>
          {hasReading && meta && <div className="tel-meta">({meta})</div>}
          {hasReading && age
            ? <div className="tel-source" style={{ color: sourceColor }}>● MANUAL · {age}{stale ? "  [flag: stale]" : ""}</div>
            : !hasReading && (
              <>
                <div className="tel-tap-hint">TAP TO ENTER READING</div>
                <div onClick={onBLERead} style={{ fontSize: "8px", color: "var(--accent-blue)", letterSpacing: "1px", marginTop: "4px", cursor: "pointer" }}>● BLE AUTO-READ</div>
              </>
            )
          }
        </>
      )}
    </div>
  );
}

// Collapsible raw-telemetry breakdown -- same GEN3 INTERCEPT fields the
// sys-log line already carries, reformatted into a legible label/value
// grid instead of one long `//`-separated line. No new data, no new
// primary tile -- just surfacing already-decoded fields more readably.
function RawTelemetryPanel({ bridge, open, onToggle, stale }) {
  // v reads bridge.vectors directly, not logic.vectors -- deliberately, not
  // an unclosed seam. logic.vectors goes through resolveVectors()'s 24h
  // freshness gate (sources.js), which exists to protect command/BRI
  // decisions from acting on stale data. This panel's job is the opposite:
  // show the actual stored value, with staleness flagged via `stale` above
  // (STALE_HRS, 12h), never suppressed. Routing this through resolveVectors()
  // would silently blank real values past 24h instead of showing them
  // flagged -- tested against a synthetic >24h bridge 2026-09, confirmed.
  // battery_pct/ibi_hr_bpm/sleep_temp_c/sleep_stages have no resolveVectors()
  // equivalent at all; the rest (rhr/spo2/step_count/sleep tiers) could be
  // added but were deliberately left ungated for the same reason above.
  const v = bridge.vectors;
  const stages = v.sleep_stages;
  const time = new Date(bridge.timestamp).toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dotColor = stale ? "var(--accent-amber)" : "var(--accent-blue)";
  return (
    <>
      <div className="telemetry-toggle" onClick={onToggle}>
        <span className="dot" style={{ background: dotColor, boxShadow: `0 0 4px ${dotColor}` }} />
        <span>{open ? "▾" : "▸"} {open ? "HIDE" : "SHOW"} RAW TELEMETRY</span>
      </div>
      {open && (
        <div className={`telemetry-panel${stale ? " tel-stale" : ""}`}>
          <div className="telemetry-inner">
            <div className="telemetry-header">
              RAW TELEMETRY // GEN3 INTERCEPT · {bridge.classifier} · [{time}]
              {stale && <span style={{ color: "var(--accent-amber)" }}> [FLAG: STALE]</span>}
            </div>
            <div className="telemetry-divider" />
            <div className="telemetry-raw-grid">
              <div><span className="label">RHR ......... </span><span className="value">{v.rhr_bpm != null ? v.rhr_bpm.toFixed(1) + " BPM" : "N/A"}</span>{v.rhr_bpm != null && <span className="telemetry-tag"> [AUTHORITATIVE]</span>}</div>
              <div><span className="label">BATTERY ..... </span><span className="value">{v.battery_pct != null ? v.battery_pct + "%" : "N/A"}</span></div>
              <div><span className="label">IBI_HR ...... </span><span className="value">{v.ibi_hr_bpm != null ? v.ibi_hr_bpm.toFixed(1) + " BPM" : "N/A"}</span>{v.ibi_hr_bpm != null && <span className="telemetry-tag"> [CROSS-CHECK]</span>}</div>
              <div><span className="label">TEMP ........ </span><span className="value">{v.sleep_temp_c != null ? v.sleep_temp_c + "°C" : "N/A"}</span></div>
              <div><span className="label">SPO2 ........ </span><span className="value">{v.spo2_avg_pct != null ? v.spo2_avg_pct + "%" : "N/A"}</span></div>
              <div><span className="label">STEPS ....... </span><span className="value">{v.step_count != null ? v.step_count : "N/A"}</span></div>
            </div>
            {stages != null && (
              <>
                <div className="telemetry-divider" />
                <div className="telemetry-stages">
                  SLEEP STAGES: WAKE {stages.wake_min}M · LIGHT {stages.light_min}M · REM {stages.rem_min}M · DEEP {stages.deep_min}M
                </div>
                {v.sleep_duration_stage_sum_hrs != null ? (
                  <div className="telemetry-stages">
                    TST (STAGE SUM, PROVISIONAL): {v.sleep_duration_stage_sum_hrs.toFixed(2)}H
                    {v.sleep_duration_stage_sum_meta?.deep_anomaly ? " [DEEP ANOMALY LOGGED]" : ""}
                  </div>
                ) : (
                  <div className="telemetry-stages">
                    TST (STAGE SUM, PROVISIONAL): -- ({v.sleep_duration_stage_sum_meta?.reason || "n/a"})
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// 2026-08-12: fills the empty space below the log with a real, already-
// decoded week -- no new fetch, no new decode work, no new trust-tier
// decision (same values already backing each tile's own "7d avg"). Raw
// values only, deliberately -- no composite score, learning directly from
// reviewing a real Oura export this session (see the CSV discussion in
// SESSION_HANDOFF.md 2026-08-12). SpO2 and steps included here specifically
// because the methuselah skill's own data-trust tiering places both at
// "weekly-trend only, log-line" -- this row-per-day log is exactly that
// surface, not the primary grid. Glucose excluded: api/glucose.js only
// ever stores the single latest reading, no history exists to show yet.
function WeeklyHistoryLog({ historyByDate }) {
  const dates = Object.keys(historyByDate || {}).sort().slice(-7);
  if (dates.length === 0) return null;
  return (
    <div className="history-log">
      <div className="history-log-header">7-DAY HISTORY // RAW VALUES ONLY</div>
      {dates.map(date => {
        const d = historyByDate[date] || {};
        return (
          <div key={date} className="history-log-line">
            <span className="history-log-date">[{date}]</span>
            <span>
              {d.hrv != null ? `HRV ${Math.round(d.hrv)}ms` : "HRV --"}
              {" · "}
              {d.rhr != null ? `RHR ${Math.round(d.rhr)}bpm` : "RHR --"}
              {" · "}
              {d.sleepDurationHrs != null ? `SLEEP ${d.sleepDurationHrs.toFixed(1)}h` : "SLEEP --"}
              {" · "}
              {d.spo2 != null ? `SPO2 ${Math.round(d.spo2)}%` : "SPO2 --"}
              {" · "}
              {d.steps != null ? `STEPS ${Math.round(d.steps)}` : "STEPS --"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// calculateBRI moved to engine/index.js — imported above

export default function MethuselahFinal() {
  const [locked,          setLocked]          = useState(true);
  const [input,           setInput]           = useState("");
  const [authError,       setAuthError]       = useState(null); // null | "wrong" | "ratelimited" | "config" | "network"
  const [glucoseReading,  setGlucoseReading]  = useState(null);
  const [glucoseTimestamp, setGlucoseTimestamp] = useState(() => localStorage.getItem("glucoseTimestamp") || null);
  const [hrvHist,   setHrvHist]   = useState(() => JSON.parse(localStorage.getItem("hrvHistory") || "[]"));
  const [rhrHist,   setRhrHist]   = useState(() => JSON.parse(localStorage.getItem("rhrHistory") || "[]"));
  const [sleepHist, setSleepHist] = useState(() =>
    JSON.parse(localStorage.getItem("sleepDurationHistory") || "[]").filter(isPlausibleSleepHours));
  const [glucHist,  setGlucHist]  = useState(() => JSON.parse(localStorage.getItem("glucoseHistory") || "[]"));
  const [spo2Hist,  setSpo2Hist]  = useState(() => JSON.parse(localStorage.getItem("spo2History") || "[]"));
  const [stepHist,  setStepHist]  = useState(() => JSON.parse(localStorage.getItem("stepHistory") || "[]"));
  // 2026-08-12: date-keyed, unlike hrvHist/rhrHist/etc above -- those are
  // independently null-filtered per field before slicing, so they can't be
  // reliably lined up by calendar date with each other (a day missing only
  // HRV would silently desync hrvHist's index from rhrHist's). Kept as its
  // own state, populated from the same fetch, purely additive -- doesn't
  // touch the existing per-field arrays or the tiles' own trend logic.
  const [historyByDate, setHistoryByDate] = useState({});
  const [glucoseEntryOpen, setGlucoseEntryOpen] = useState(false);
  const [glucoseInput,    setGlucoseInput]    = useState("");
  const [execState,       setExecState]       = useState("idle");
  const [briefingOpen,    setBriefingOpen]    = useState(false);
  const [rawTelemetryOpen, setRawTelemetryOpen] = useState(false);
  const [gen3Bridge,      setGen3Bridge]      = useState(null);
  const [logs,            setLogs]            = useState([]);
  const [page,            setPage]            = useState("execution"); // "execution" | "calibration"
  const logRef = useRef(null);

  const addLog = (msg, type = "", color = null) => setLogs(prev => [{ time: ts(), msg, type, color }, ...prev].slice(0, 12));

  // The actual comparison happens server-side (api/auth.js) -- this used to
  // be `if (input === MASTER_KEY) unlock()` with MASTER_KEY hardcoded right
  // here, which meant the real password shipped inside the public JS bundle
  // every visitor's browser downloads. Never actually secret, just a speed
  // bump. Now the guess is sent to the server and only a pass/fail comes
  // back -- the real key lives only in a Vercel env var.
  const attemptUnlock = async () => {
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: input }),
      });
      if (res.ok) { sessionStorage.setItem("dashboardKey", input); unlock(); return; }
      // A wrong key and a missing DASHBOARD_ACCESS_KEY (server misconfigured)
      // used to render identical text -- a real config mistake would have
      // looked exactly like a typo, with no signal pointing at the real
      // cause. api/auth.js returns 401 for a wrong key, 429 once rate-
      // limited (2026-09-21), 500 for anything else -- falling a 429 into
      // "config" would tell the real owner their own server is broken
      // during the exact moment the limiter is correctly protecting them.
      setAuthError(res.status === 401 ? "wrong" : res.status === 429 ? "ratelimited" : "config");
      setInput("");
    } catch {
      setAuthError("network");
      setInput("");
    }
  };

  // There's no server-side session to invalidate -- authedFetch resends the
  // raw DASHBOARD_ACCESS_KEY on every call, it's never exchanged for a
  // server-issued token (see api/_authCheck.js). Signing out just forgets
  // this browser's copy of the key; it does not and cannot revoke the key
  // itself -- that only happens by rotating DASHBOARD_ACCESS_KEY in Vercel.
  const signOut = () => {
    sessionStorage.removeItem("dashboardKey");
    setLocked(true);
    setInput("");
    setAuthError(null);
  };

  const unlock = () => {
    setLocked(false);
    setAuthError(null);
    const today = new Date().toLocaleDateString("en-CA");
    const protocolDate = localStorage.getItem("protocolExecutedDate");
    if (protocolDate === today) {
      setExecState("satisfied");
    }
    const storedDate    = localStorage.getItem("glucoseDate");
    const storedReading = localStorage.getItem("glucoseReading");
    if (storedDate === today && storedReading) {
      setGlucoseReading(parseFloat(storedReading));
    }
  };

  const readBLEGlucose = async () => {
    try {
      addLog("BLE INTERCEPT // SCANNING FOR METHUSELAH BRIDGE...", "event");
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ name: "METHUSELAH" }],
        optionalServices: ["4fafc201-1fb5-459e-8fcc-c5c9c331914b"]
      });
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService("4fafc201-1fb5-459e-8fcc-c5c9c331914b");
      const characteristic = await service.getCharacteristic("beb5483e-36e1-4688-b7f5-ea07361b26a8");
      const value = await characteristic.readValue();
      const rawBytes = new Uint8Array(value.buffer);
      const text = new TextDecoder().decode(value);
      addLog("BLE RAW BYTES: " + Array.from(rawBytes).join(","), "roche");
      addLog("BLE TEXT: " + text, "roche");
      const parts = text.split(",");
      const glucose = parseFloat(parts[0]);
      if (!isNaN(glucose) && glucose > 0.5 && glucose < 30) {
        setGlucoseReading(glucose);
        const today = new Date().toLocaleDateString("en-CA");
        const nowIso = new Date().toISOString();
        localStorage.setItem("glucoseReading", glucose.toString());
        localStorage.setItem("glucoseDate", today);
        localStorage.setItem("glucoseTimestamp", nowIso);
        setGlucoseTimestamp(nowIso);
        const newGlucHist = [...glucHist, glucose].slice(-7);
        setGlucHist(newGlucHist);
        localStorage.setItem("glucoseHistory", JSON.stringify(newGlucHist));
        pushGlucoseToServer(glucose, nowIso);
        setGlucoseEntryOpen(false);
        setGlucoseInput("");
        addLog("BLE INTERCEPT: " + glucose.toFixed(1) + " MMOL/L // AUTO-LOGGED", "roche");
        const bri = calculateBRI({ glucose, hrv, rhr, sleepDurationHrs, glucosePending: false });
        addLog("BIOLOGICAL READINESS INDEX: " + bri.score + " // " + bri.label);
      } else {
        addLog("BLE // NO READING YET — ENTER MANUALLY", "event");
      }
      device.gatt.disconnect();
    } catch (err) {
      addLog("BLE // " + err.message, "event");
    }
  };

    const submitGlucose = () => {
    const val = parseFloat(glucoseInput);
    if (isNaN(val) || val < 1 || val > 30) return;
    setGlucoseReading(val);
    const today = new Date().toLocaleDateString("en-CA");
    const nowIso = new Date().toISOString();
    localStorage.setItem("glucoseReading", val.toString());
    localStorage.setItem("glucoseDate", today);
    localStorage.setItem("glucoseTimestamp", nowIso);
    setGlucoseTimestamp(nowIso);
    const newGlucHist = [...glucHist, val].slice(-7);
    setGlucHist(newGlucHist);
    localStorage.setItem("glucoseHistory", JSON.stringify(newGlucHist));
    pushGlucoseToServer(val, nowIso);
    addLog(`MANUAL GLUCOSE: ${val.toFixed(1)} MMOL/L`, "roche");
    const briGlucose = calculateBRI({ glucose: val, hrv, rhr, sleepDurationHrs, glucosePending: false });
    addLog(`BIOLOGICAL READINESS INDEX: ${briGlucose.score} // ${briGlucose.label}`, "", briGlucose.color);
    setGlucoseEntryOpen(false);
    setGlucoseInput("");
  };

  useEffect(() => {
  }, []);

  useEffect(() => {
    // Gated on `locked`, not just []: this fetch now requires a real key
    // (authedFetch), which doesn't exist yet on first mount, before login.
    // Re-running when `locked` flips to false is what makes it fetch real
    // data the moment a valid key exists, instead of failing once on mount
    // and only recovering up to 5 minutes later via the interval below.
    if (locked) return;
    const fetchBridge = () =>
      authedFetch('/api/gen3-bridge')
        .then(res => res.ok ? res.json() : null)
        .catch(() => null)
        .then(data => { if (data && data.source === 'gen3_ble') setGen3Bridge(data); });
    fetchBridge();
    const id = setInterval(fetchBridge, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(id);
  }, [locked]);

  // Cross-device glucose sync (2026-08-02) -- localStorage never leaves the
  // browser it was written in, so a reading entered on one device was
  // invisible on any other. Adopt the server's reading if it's for today
  // and newer than whatever this device already has (covers "entered on
  // another device after this one last loaded"); submitGlucose/
  // readBLEGlucose below push new readings back up so other devices pick
  // them up on their own next fetch.
  useEffect(() => {
    if (locked) return; // see the gen3-bridge effect above for why
    authedFetch('/api/glucose')
      .then(res => res.ok ? res.json() : null)
      .catch(() => null)
      .then(data => {
        if (!data || data.value == null || !data.timestamp) return;
        const today = new Date().toLocaleDateString("en-CA");
        const serverDate = new Date(data.timestamp).toLocaleDateString("en-CA");
        if (serverDate !== today) return;
        const localTimestamp = localStorage.getItem("glucoseTimestamp");
        if (!localTimestamp || new Date(data.timestamp) > new Date(localTimestamp)) {
          setGlucoseReading(data.value);
          setGlucoseTimestamp(data.timestamp);
          localStorage.setItem("glucoseReading", data.value.toString());
          localStorage.setItem("glucoseDate", today);
          localStorage.setItem("glucoseTimestamp", data.timestamp);
        }
      });
  }, [locked]);

  const pushGlucoseToServer = (value, timestamp) => {
    authedFetch('/api/glucose', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value, timestamp }),
    }).catch(() => {}); // best-effort -- local state/localStorage already updated regardless
  };

  // Cross-device history sync for HRV/RHR/Sleep/SpO2/Steps (2026-08-02) --
  // same problem and same fix as glucose above: each device was building its
  // own separate 7-day history purely from localStorage, so the SAME live
  // value showed a DIFFERENT 7d avg/trend on a laptop vs. a phone (confirmed
  // side-by-side: 47ms vs 38ms HRV avg, 69 vs 62bpm RHR avg, 33.7h vs 7.6h
  // sleep avg, all for the identical live reading). Adopts the server's
  // history as authoritative on load whenever it has any entries -- the
  // "Update 7-day histories" effect below pushes each day's values back up
  // so every device converges on the same shared history.
  useEffect(() => {
    if (locked) return; // see the gen3-bridge effect above for why
    authedFetch('/api/vector-history')
      .then(res => res.ok ? res.json() : null)
      .catch(() => null)
      .then(history => {
        if (!history || typeof history !== 'object') return;
        const dates = Object.keys(history).sort();
        const extract = (field) => dates.map(d => history[d][field]).filter(v => v != null).slice(-7);
        const fromServer = {
          hrv: extract('hrv'),
          rhr: extract('rhr'),
          sleepDurationHrs: extract('sleepDurationHrs').filter(isPlausibleSleepHours),
          spo2: extract('spo2'),
          steps: extract('steps'),
        };
        if (fromServer.hrv.length)              { setHrvHist(fromServer.hrv);        localStorage.setItem("hrvHistory", JSON.stringify(fromServer.hrv)); }
        if (fromServer.rhr.length)              { setRhrHist(fromServer.rhr);        localStorage.setItem("rhrHistory", JSON.stringify(fromServer.rhr)); }
        if (fromServer.sleepDurationHrs.length)  { setSleepHist(fromServer.sleepDurationHrs); localStorage.setItem("sleepDurationHistory", JSON.stringify(fromServer.sleepDurationHrs)); }
        if (fromServer.spo2.length)              { setSpo2Hist(fromServer.spo2);      localStorage.setItem("spo2History", JSON.stringify(fromServer.spo2)); }
        if (fromServer.steps.length)             { setStepHist(fromServer.steps);     localStorage.setItem("stepHistory", JSON.stringify(fromServer.steps)); }
        setHistoryByDate(history);
      });
  }, [locked]);

  // Update 7-day histories from Gen3 bridge — once per bridge date.
  // Writes to localStorage (fast local cache) AND pushes to the shared
  // /api/vector-history store (2026-08-02) so every device converges on
  // the same history instead of each building its own -- see the fetch-on-
  // mount effect above. The server write is a same-date merge (see
  // api/vector-history.js), so multiple devices recording "today" is
  // harmless, not duplicated.
  //
  // v reads gen3Bridge.vectors directly, same deliberate ungating as
  // RawTelemetryPanel above, same reason: logic.vectors goes through
  // resolveVectors()'s 24h freshness gate, and a 7-day trend that goes
  // blank at 24h (instead of just not adding today's point) would be
  // actively misleading right next to the command it sits beside. This
  // effect also runs before `logic` exists in this component's render order.
  useEffect(() => {
    if (!gen3Bridge?.vectors || !gen3Bridge.timestamp) return;
    const bridgeDate = new Date(gen3Bridge.timestamp).toLocaleDateString("en-CA");
    if (localStorage.getItem("lastBridgeHistoryDate") === bridgeDate) return;
    const v = gen3Bridge.vectors;
    const serverPayload = { date: bridgeDate };
    if (v.hrv_ms != null) {
      const h = [...hrvHist, v.hrv_ms].slice(-7);
      setHrvHist(h); localStorage.setItem("hrvHistory", JSON.stringify(h));
      serverPayload.hrv = v.hrv_ms;
      addLog(`GEN3 HRV: ${Math.round(v.hrv_ms)} MS`, "roche");
    }
    if (v.rhr_bpm != null) {
      const h = [...rhrHist, v.rhr_bpm].slice(-7);
      setRhrHist(h); localStorage.setItem("rhrHistory", JSON.stringify(h));
      serverPayload.rhr = v.rhr_bpm;
      addLog(`GEN3 RHR: ${Math.round(v.rhr_bpm)} BPM`, "roche");
    }
    const sleepForHistory = v.sleep_duration_hrs ?? v.sleep_duration_estimate_hrs;
    if (sleepForHistory != null && isPlausibleSleepHours(sleepForHistory)) {
      const h = [...sleepHist, sleepForHistory].slice(-7);
      setSleepHist(h); localStorage.setItem("sleepDurationHistory", JSON.stringify(h));
      serverPayload.sleepDurationHrs = sleepForHistory;
      const estFlag = v.sleep_duration_hrs == null ? " (EST)" : "";
      addLog(`GEN3 SLEEP: ${sleepForHistory.toFixed(1)}H${estFlag}`, "roche");
    }
    if (v.spo2_avg_pct != null) {
      const h = [...spo2Hist, v.spo2_avg_pct].slice(-7);
      setSpo2Hist(h); localStorage.setItem("spo2History", JSON.stringify(h));
      serverPayload.spo2 = v.spo2_avg_pct;
      const avg = h.reduce((a, b) => a + b, 0) / h.length;
      const trend = h.length >= 2 ? (h[h.length-1] - h[0] > 0.3 ? "TRENDING UP" : h[0] - h[h.length-1] > 0.3 ? "TRENDING DOWN" : "STABLE") : "BUILDING";
      addLog(`GEN3 SPO2 7D AVG: ${avg.toFixed(1)}% // ${trend} // NOTE: GEN3 READS ~3-5% LOW`, "roche");
    }
    if (v.step_count != null && v.step_count > 0) {
      const h = [...stepHist, v.step_count].slice(-7);
      setStepHist(h); localStorage.setItem("stepHistory", JSON.stringify(h));
      serverPayload.steps = v.step_count;
      const avg = Math.round(h.reduce((a, b) => a + b, 0) / h.length);
      const trend = h.length >= 2 ? (h[h.length-1] - h[0] > 200 ? "TRENDING UP" : h[0] - h[h.length-1] > 200 ? "TRENDING DOWN" : "STABLE") : "BUILDING";
      addLog(`GEN3 STEPS 7D AVG: ${avg}/DAY // ${trend}`, "roche");
    }
    if (Object.keys(serverPayload).length > 1) {
      authedFetch('/api/vector-history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(serverPayload),
      }).catch(() => {}); // best-effort -- local state/localStorage already updated regardless
    }
    localStorage.setItem("lastBridgeHistoryDate", bridgeDate);
  }, [gen3Bridge]);

  const logic = evaluateSources(null, gen3Bridge, { glucose: glucoseReading });
  const { hrv, rhr, sleepDurationHrs } = {
    hrv: logic.vectors.hrv.value,
    rhr: logic.vectors.rhr.value,
    sleepDurationHrs: logic.vectors.sleepDurationHrs.value,
  };
  const sleepEstimateMethod = logic.vectors.sleepDurationHrs.estimateMethod ?? null;
  const sleepIsEstimate = sleepEstimateMethod !== null;

  useEffect(() => { setBriefingOpen(false); }, [logic.level]);

  // Per-vector timestamps — Gen4 dead, all vectors from Gen3 bridge timestamp
  const gen3Ts  = gen3Bridge?.timestamp ?? null;
  const hrvTs   = logic.vectors.hrv.source   === SOURCE_GEN3 ? gen3Ts : null;
  const rhrTs   = logic.vectors.rhr.source   === SOURCE_GEN3 ? gen3Ts : null;
  // Prefer the sleep-data-specific timestamp (real freshness of the 0x4C
  // decode this value came from) over the bridge's general timestamp (which
  // reflects HRV/RHR refreshing live every cycle, not sleep data at all) --
  // falls back to the old behavior if a bridge predates this field.
  const sleepTs = logic.vectors.sleepDurationHrs.measuredAt
    ?? (logic.vectors.sleepDurationHrs.source === SOURCE_GEN3 ? gen3Ts : null);

  // Trend + avg per vector (histories come from state, seeded from localStorage on mount)
  const hrvAvg   = avgOf(hrvHist);
  const rhrAvg   = avgOf(rhrHist);
  const sleepAvg = avgOf(sleepHist);
  const glucAvg  = avgOf(glucHist);

  const hrvColor = hrv === null ? "var(--text-dim)" : hrv < THRESHOLDS.hrv ? "var(--accent-amber)" : "var(--accent-green)";
  const rhrColor = rhr === null ? "var(--text-dim)" : rhr > THRESHOLDS.rhr ? "var(--accent-amber)" : "var(--accent-green)";
  const sleepColor = sleepDurationHrs === null ? "var(--text-dim)" : sleepDurationHrs < THRESHOLDS.sleepDurationCritical ? "var(--accent-red)" : sleepDurationHrs < THRESHOLDS.sleepDurationWarn ? "var(--accent-amber)" : "var(--accent-green)";
  // Sleep carries two thresholds (Warn/Critical), one tile. Show whichever
  // is currently firing; default to Warn when passing -- the first line
  // actually crossed coming down from optimal, not the rarer Critical case.
  // Explicit ruling, not a guess: 2026-08-21, see known_issues.md.
  const sleepThresholdKey = sleepDurationHrs !== null && sleepDurationHrs < THRESHOLDS.sleepDurationCritical
    ? "sleepDurationCritical" : "sleepDurationWarn";

  const hrvMeta   = hrv              !== null ? metaLine(predicateText("HRV", "hrv", "ms"), hrvColor, hrvAvg !== null ? `7d avg ${Math.round(hrvAvg)}ms` : null, getTrend(hrvHist)) : null;
  const rhrMeta   = rhr              !== null ? metaLine(predicateText("RHR", "rhr", "bpm"), rhrColor, rhrAvg !== null ? `7d avg ${Math.round(rhrAvg)}bpm` : null, getTrend(rhrHist)) : null;
  const sleepMeta = sleepDurationHrs !== null ? metaLine(predicateText("SLEEP", sleepThresholdKey, "h"), sleepColor, sleepAvg !== null ? `7d avg ${sleepAvg.toFixed(1)}h` : null, getTrend(sleepHist)) : null;

  const handleExecute = () => {
    setExecState("active");
  };

  const handleComplete = () => {
    setExecState("complete");
    setTimeout(() => {
      const today = new Date().toLocaleDateString("en-CA");
      localStorage.setItem("protocolExecutedDate", today);
      setExecState("satisfied");
    }, 3000);
  };

  const gen3Live = gen3Bridge?.timestamp && !isStale(gen3Bridge.timestamp);
  // 2026-09-15/20, two owner corrections in sequence: the badge names the
  // physical device (an Oura ring, whichever generation) and never changes
  // -- "OURA", full stop, not "OURA LIVE"/"GEN3"/"OFFLINE" text variants.
  // Connection state is carried by color alone, and collapsed to exactly
  // two states per the second correction (not three): blue = connected,
  // amber = not connected (this covers both "present but stale" and
  // "never present" -- there is no separate dim/offline color anymore).
  const badgeColor = gen3Live ? "var(--accent-blue)" : "var(--accent-amber)";
  const badgeLabel = "OURA";

  return (
    <>
      <style>{CSS}</style>

      {locked ? (
        <div className="auth-overlay">
          <div className="auth-title">METHUSELAH // ACCESS REQUIRED</div>
          <input
            className="auth-input"
            type="password"
            value={input}
            onChange={e => { setInput(e.target.value); setAuthError(null); }}
            onKeyDown={e => {
              if (e.key !== "Enter") return;
              attemptUnlock();
            }}
            placeholder="********"
          />
          <div className="auth-hint">INPUT MASTER KEY → PRESS RETURN</div>
          <button
            className="auth-decrypt"
            onClick={attemptUnlock}
          >
            ENTER
          </button>
          {authError === "wrong" && <div className="auth-error">⚠ ACCESS DENIED // INVALID KEY</div>}
          {authError === "ratelimited" && <div className="auth-error">⚠ TOO MANY ATTEMPTS // TRY AGAIN IN A FEW MINUTES</div>}
          {authError === "config" && <div className="auth-error">⚠ SERVER MISCONFIGURED // DASHBOARD_ACCESS_KEY NOT SET</div>}
          {authError === "network" && <div className="auth-error">⚠ NETWORK ERROR // COULD NOT REACH SERVER</div>}
        </div>
      ) : (
        <div className="shell" style={{ minHeight: "100vh", height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div className="header">
            <div className="brand-wrap">
              <div className="brand">METHUSELAH</div>
            </div>
            <div className="header-right">
              <div className="header-top-row">
                <div className="live-badge" style={{ color: badgeColor }}>
                  <div className="blink" style={{ background: badgeColor }} />
                  {badgeLabel}
                </div>
                <button className="btn-signout" onClick={signOut}>SIGN OUT</button>
              </div>
            </div>
          </div>

          {page === "execution" && (
          <>
          <div className="telemetry-grid">
            <GlucosePanel
              reading={glucoseReading}
              entryOpen={glucoseEntryOpen}
              inputVal={glucoseInput}
              avg={glucAvg}
              trend={getTrend(glucHist)}
              age={formatAge(glucoseTimestamp)}
              stale={isStale(glucoseTimestamp)}
              onTap={() => setGlucoseEntryOpen(true)}
              onBLERead={readBLEGlucose}
              onInputChange={e => setGlucoseInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") submitGlucose(); if (e.key === "Escape") { setGlucoseEntryOpen(false); setGlucoseInput(""); } }}
              onSubmit={submitGlucose}
            />
            <Metric
              label="HRV"
              val={hrv !== null ? Math.round(hrv) : "--"}
              unit="ms"
              color={hrvColor}
              meta={hrvMeta}
              age={formatAge(hrvTs)}
              stale={isStale(hrvTs)}
              source={logic.vectors.hrv.source}
              n={logic.vectors.hrv.n}
              agg={logic.vectors.hrv.agg}
            />
            <Metric
              label="CARDIAC LOAD"
              val={rhr !== null ? rhr : "--"}
              unit="bpm"
              color={rhrColor}
              meta={rhrMeta}
              age={formatAge(rhrTs)}
              stale={isStale(rhrTs)}
              source={logic.vectors.rhr.source}
              n={logic.vectors.rhr.n}
              agg={logic.vectors.rhr.agg}
            />
            <Metric
              label="SLEEP DURATION"
              val={sleepDurationHrs !== null ? `${sleepIsEstimate ? "~" : ""}${sleepDurationHrs.toFixed(1)}` : "--"}
              unit={sleepEstimateMethod === "stage_sum" ? "hrs (stage sum)" : sleepEstimateMethod === "bout_tail" ? "hrs (est.)" : "hrs"}
              color={sleepColor}
              meta={sleepMeta}
              age={formatAge(sleepTs)}
              stale={isStale(sleepTs)}
              source={logic.vectors.sleepDurationHrs.source}
              agg={logic.vectors.sleepDurationHrs.agg}
            />
          </div>

          {/* 2026-08-15/16: execState-driven green is a scoped, named exception to
              the project's own no-reward-mechanic rule -- covers ONLY this border
              and its own meta label below, nothing else (not tiles, not the BRI
              score, not any future element). Persists until local midnight by
              design (see unlock()'s protocolExecutedDate check) -- an explicit,
              deliberate call, not an oversight: a real breach after completion
              does NOT pull the border back to logic.border same-day. Full
              reasoning in known_issues.md, 2026-08-15/16 entries.

              2026-09-15: border was bri.color (the composite BRI score) until
              today -- the command TEXT already came from logic (the single-
              vector cascade), so the panel could show amber command text next
              to a green BRI-driven border, two disagreeing signals on the same
              element. logic.border already existed for exactly this (commands.js,
              one per command, paired with but distinct from logic.color) and was
              simply unused. Swapped in -- border and text now always agree,
              and BRI stays exactly where design rule #1 puts it: a log line only.
              See known_issues.md, 2026-09-15 (fable_master_audit_2026-07-24.md,
              item B3, flagged this same drift two months ago). */}
          <div className="command-wrap" style={{ borderColor: execState === "satisfied" ? "#00ff66" : logic.border }}>
            <div className="corner tl" /><div className="corner tr" />
            <div className="corner bl" /><div className="corner br" />
            <div className="cmd-meta">{execState === "satisfied" ? "SATISFIED" : logic.level.toUpperCase()} // <Clock /></div>
            {execState === "idle" ? (
              <>
                <div
                  className="cmd-text"
                  style={{ color: logic.color, cursor: "pointer" }}
                  onClick={() => setBriefingOpen(o => !o)}
                >
                  {logic.cmd}
                </div>
                {briefingOpen && (
                  <div className="cmd-briefing">{logic.briefing}</div>
                )}
                <div className="cmd-rationale">{logic.rat}</div>
                {logic.level === "awaiting" ? (
                  <div className="tel-tap-hint">NO DATA — RUN DAEMON OR MORNING PULL</div>
                ) : logic.level !== "optimal" ? (
                  <button className="btn-execute" onClick={handleExecute}>
                    EXECUTE PROTOCOL
                  </button>
                ) : (
                  <div className="optimal-label">BASELINE STABLE.</div>
                )}
              </>
            ) : execState === "active" ? (
              <>
                <div className="cmd-text" style={{ color: logic.color }}>PROTOCOL ACTIVE.</div>
                <button className="btn-execute" onClick={handleComplete}>
                  PROTOCOL COMPLETE
                </button>
              </>
            ) : execState === "complete" ? (
              <div className="cmd-text" style={{ color: logic.color }}>PROTOCOL COMPLETE.</div>
            ) : (
              // satisfied -- 2026-08-16: previously also rendered a "WEEKLY
              // PATTERN // 7-DAY REVIEW" block here (SpO2 avg, steps/day, sleep
              // onset) -- three non-vectors filling the dashboard's most
              // protected region. Cut outright, not relocated (see
              // known_issues.md 2026-08-16): it only ever existed inside this
              // transient post-completion state, never a real standing 7-day
              // surface, so there was nothing stable to move.
              <div className="cmd-rationale" style={{ color: "var(--text-dim)" }}>PROTOCOL EXECUTED // RETURN TOMORROW</div>
            )}
          </div>

          {gen3Bridge && (
            <RawTelemetryPanel
              bridge={gen3Bridge}
              open={rawTelemetryOpen}
              onToggle={() => setRawTelemetryOpen(o => !o)}
              stale={isStale(gen3Bridge.timestamp)}
            />
          )}

          <div className={`sys-log${logs.length === 0 ? " sys-log-empty" : ""}`} ref={logRef}>
            {logs.map((l, i) => (
              <div key={i} className="log-line">
                <span className="log-time">[{l.time}]</span>
                <span
                  className={l.type === "roche" ? "log-roche" : ""}
                  style={l.color ? { color: l.color } : undefined}
                >
                  {l.msg}{i === 0 && <span className="log-cursor" />}
                </span>
              </div>
            ))}
          </div>

          <WeeklyHistoryLog historyByDate={historyByDate} />
          </>
          )}

          {page === "calibration" && <CalibrationPage authedFetch={authedFetch} />}

          <nav className="bottom-nav">
            <button
              className={`bottom-nav-tab${page === "execution" ? " active" : ""}`}
              onClick={() => setPage("execution")}
            >
              EXECUTION
            </button>
            <button
              className={`bottom-nav-tab${page === "calibration" ? " active" : ""}`}
              onClick={() => setPage("calibration")}
            >
              CALIBRATION
            </button>
          </nav>
        </div>
      )}
    </>
  );
}
